/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { PopupClose } from '../Icon/PopupClose'
import ImageOptimize from '../ImageOptimize'

import {
  AddToCartType,
  CouponTarget,
  Languages,
  VoucherTypes,
} from '@/constants'
import { useAuth, useCart, useStore } from '@/containers'
import type { Cart, VoucherItem } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { collectProductIds } from '@/utils/cart-helpers'
import { convertCoupon } from '@/utils/next-util'
import { Listbox } from '@headlessui/react'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import { FormButton } from '../Form'
import { Loading } from '../Loading/Loading'
import PopupBody from './PopupBody'
import PopupFrame from './PopupFrame'
import PopupTitle from './PopupTitle'

interface ModalVoucherList {
  showListCoupons: boolean
  setShowListCoupons: Dispatch<SetStateAction<boolean>>
  shippingMethod?: string
}

const ModalVoucherList = ({
  showListCoupons,
  setShowListCoupons,
  shippingMethod,
}: ModalVoucherList) => {
  const t = useTranslations('Cart')
  const router = useRouter()
  const tCommon = useTranslations('Common')
  const {
    getCartCoupons,
    cartCoupons,
    setCouponApply,
    couponApply,
    clearAutoVoucherState,
    setVoucherApply,
    voucherApply,
  } = useCart()
  const { selectedLang } = useStore()
  const { fetchCart, setErrorAuth, errorAuth, cart } = useAuth()

  const [couponSelected, setCouponSelected] = useState<string[]>([])
  const [inputVoucher, setInputVoucher] = useState('')
  const [inputVoucherError, setInputVoucherError] = useState('')
  const [isApply, setIsApply] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [pendingVouchers, setPendingVouchers] = useState<string[]>([])

  const appliedVoucherSnapshot = useMemo(() => {
    const active = voucherApply?.length > 0 ? voucherApply : couponApply
    return Array.from(new Set((active || []).filter(Boolean)))
  }, [couponApply, voucherApply])

  const resetFormState = useCallback(() => {
    setInputVoucher('')
    setInputVoucherError('')
    setIsApply(false)
    setPendingVouchers([])
    setErrorAuth({
      status: false,
      message: '',
      key: '',
    })
  }, [setErrorAuth])

  useEffect(() => {
    if (!showListCoupons) return

    resetFormState()
    setCouponSelected(prev => {
      if (
        prev.length === appliedVoucherSnapshot.length &&
        prev.every(item => appliedVoucherSnapshot.includes(item))
      ) {
        return prev
      }
      return appliedVoucherSnapshot
    })
    const currentProductIds = collectProductIds(cart?.products)
    getCartCoupons(currentProductIds.length ? currentProductIds : undefined)
  }, [
    showListCoupons,
    appliedVoucherSnapshot,
    getCartCoupons,
    resetFormState,
    cart?.products,
  ])
  const currency = (lang: string) => {
    switch (lang) {
      case 'vi':
        return 'VND'
      case 'en':
        return 'USD'
      default:
        return 'VND'
    }
  }

  const validateVoucherInput = useCallback(
    (voucher: string): { isValid: boolean; message: string } => {
      const trimmedVoucher = voucher.trim().toUpperCase()

      if (!trimmedVoucher) {
        return { isValid: false, message: 'Mã voucher không được để trống' }
      }

      if (trimmedVoucher.length < 3) {
        return { isValid: false, message: 'Mã voucher phải có ít nhất 3 ký tự' }
      }

      if (trimmedVoucher.length > 30) {
        return { isValid: false, message: 'Mã voucher không được quá 30 ký tự' }
      }

      const voucherPattern = /^[A-Z0-9_-]{3,20}$/
      if (!voucherPattern.test(trimmedVoucher)) {
        return {
          isValid: false,
          message: 'Mã voucher chỉ được chứa chữ cái, số và ký tự _, -',
        }
      }

      const allSelectedVouchers = [...couponSelected, ...pendingVouchers]
      if (allSelectedVouchers.includes(trimmedVoucher)) {
        return { isValid: false, message: 'Mã voucher này đã được chọn' }
      }

      return { isValid: true, message: '' }
    },
    [couponSelected, pendingVouchers]
  )

  function checkVoucherType(voucher: VoucherItem) {
    switch (voucher.type) {
      case VoucherTypes.PERCENT:
        return voucher.percentValue + '%'
      case VoucherTypes.FIXED:
        return voucher.fixedValue
          ?.toLocaleString(selectedLang as string, {
            style: 'currency',
            currency: currency(selectedLang as string),
          })
          .replace(/\s₫/g, 'đ')
      default:
        break
    }
  }

  const updateVoucher = useCallback(
    async (code: string[], manually = false) => {
      setIsProcessing(true)

      try {
        const res: Cart = await fetchCart({
          coupon: code,
          shippingMethod,
          action: 'popup discount 1',
        })

        setErrorAuth({
          status: false,
          message: '',
          key: '',
        })

        const coupons = res?.coupons ?? []

        if (coupons.filter(e => e.mes).length > 0) {
          const errorCoupons = coupons.filter(e => e.mes)
          const validCoupons = coupons.filter(e => !e.mes)

          errorCoupons.forEach(errorCoupon => {
            toast.error(
              `${errorCoupon.coupon.couponCode}: ${errorCoupon.mes}`,
              {
                position: 'bottom-center',
                autoClose: 4000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                progress: undefined,
                theme: 'light',
              }
            )
          })

          if (validCoupons.length > 0) {
            const couponApplied = validCoupons
              .map(item => item?.coupon?.initialKey)
              .filter((code): code is string => Boolean(code))
            setCouponSelected(couponApplied)
            setCouponApply(couponApplied)
            setVoucherApply(couponApplied)
          }

          if (manually && validCoupons.length > 0) {
            setShowListCoupons(false)
            setInputVoucher('')
            setInputVoucherError('')
          }
        } else {
          const couponApplied = coupons
            .map(item => item?.coupon?.initialKey)
            .filter((code): code is string => Boolean(code))
          setCouponSelected(couponApplied)
          setCouponApply(couponApplied)
          setVoucherApply(couponApplied)

          if (manually) {
            setShowListCoupons(false)
            setInputVoucher('')
            setInputVoucherError('')
          }

          toast.success('Áp dụng mã khuyến mại thành công!', {
            position: 'bottom-center',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: 'light',
          })
        }

        getCartCoupons()
      } catch (error) {
        console.error('Error updating voucher:', error)
        if (manually) {
          setShowListCoupons(false)
          setInputVoucher('')
          setInputVoucherError('')
        }

        toast.error('Áp dụng voucher thất bại!', {
          position: 'bottom-center',
          autoClose: 4000,
        })
      } finally {
        setIsProcessing(false)
        setPendingVouchers([])
      }
    },
    [
      shippingMethod,
      fetchCart,
      setErrorAuth,
      setCouponApply,
      getCartCoupons,
      setShowListCoupons,
    ]
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setInputVoucher(value)

      if (inputVoucherError) {
        setInputVoucherError('')
      }

      setIsApply(!!value.trim())
    },
    [inputVoucherError]
  )

  const applyInputVoucher = useCallback(async () => {
    const validation = validateVoucherInput(inputVoucher)

    if (!validation.isValid) {
      setInputVoucherError(validation.message)
      return
    }

    const trimmedVoucher = inputVoucher.trim().toUpperCase()
    const newVouchers = [...couponSelected, trimmedVoucher]

    setPendingVouchers([trimmedVoucher])

    await updateVoucher(newVouchers, true)
  }, [inputVoucher, validateVoucherInput, couponSelected, updateVoucher])

  const handleInputKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        if (isApply && !isProcessing) {
          applyInputVoucher()
        }
      }
    },
    [isApply, isProcessing, applyInputVoucher]
  )
  const clearCoupon = useCallback(() => {
    setCouponApply([])
    setCouponSelected([])
    setInputVoucher('')
    setInputVoucherError('')
    setVoucherApply([])
    setPendingVouchers([])
    setIsApply(false)
    clearAutoVoucherState({ skipAutoApply: true })
    setErrorAuth({
      status: false,
      message: '',
      key: '',
    })

    fetchCart({
      coupon: [],
      shippingMethod,
      ...(router?.query?.type === AddToCartType.PRE_ORDER &&
        router?.query?.id && {
          productIds: router?.query?.id as string | undefined,
        }),
      ...(router?.query?.type === AddToCartType.INSTALLMENT &&
        router?.query?.id && {
          productIds: (router?.query?.id + ':1') as string | undefined,
        }),
      action: 'clear all vouchers',
    })
      .then(() => {
        getCartCoupons()
        setShowListCoupons(false)
        toast.success('Đã xóa tất cả voucher thành công!', {
          position: 'bottom-center',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
      })
      .catch(() => {
        toast.error('Xóa voucher thất bại!', {
          position: 'bottom-center',
          autoClose: 2000,
        })
      })
  }, [
    setCouponApply,
    setCouponSelected,
    setVoucherApply,
    fetchCart,
    getCartCoupons,
    shippingMethod,
    router?.query,
    setErrorAuth,
  ])

  const cardCouponsMapping = useMemo(() => {
    if (!cartCoupons?.length) return []
    return cartCoupons.reduce((acc: any, cur: VoucherItem) => {
      if (cur.target === CouponTarget.PRODUCT) {
        const productGroup = acc.find((e: any) => e._id === cur.product?._id)
        const couponApply = cart?.coupons?.find(
          coupon =>
            coupon.coupon?.initialKey ===
            convertCoupon(cur.code, cur.product?._id)
        )
        if (productGroup) {
          productGroup.coupons.push({
            ...cur,
            ...(couponApply && {
              mes: couponApply.mes,
            }),
          })
          return acc
        }
        return [
          ...acc,
          {
            ...cur.product,
            coupons: [
              {
                ...cur,
                ...(couponApply && {
                  mes: couponApply.mes,
                }),
              },
            ],
          },
        ]
      }

      const couponApply = cart?.coupons?.find(
        coupon => coupon.coupon?.initialKey === `${cur.code}_orderCoupon`
      )
      return [
        ...acc,
        {
          name: {
            vi: 'Áp dụng cho đơn hàng',
          },
          _id: 'orderCoupon',
          coupons: [
            {
              ...cur,
              ...(couponApply && {
                mes: couponApply.mes,
              }),
            },
          ],
        },
      ]
    }, [] as any)
  }, [cartCoupons, cart])

  const availableVoucherKeys = useMemo(() => {
    if (!cardCouponsMapping.length) return []

    return cardCouponsMapping.flatMap((group: any) => {
      if (!group?.coupons?.length) return []

      return group.coupons
        .map((coupon: VoucherItem) =>
          convertCoupon(coupon.code, group._id || 'orderCoupon')
        )
        .filter(
          (key: string | undefined): key is string => typeof key === 'string'
        )
    })
  }, [cardCouponsMapping])

  useEffect(() => {
    if (!showListCoupons) return
    if (!availableVoucherKeys.length) {
      setCouponSelected([])
      setPendingVouchers([])
      return
    }

    setCouponSelected(prev =>
      prev.filter(voucherKey => availableVoucherKeys.includes(voucherKey))
    )
    setPendingVouchers(prev =>
      prev.filter(voucherKey => availableVoucherKeys.includes(voucherKey))
    )
  }, [availableVoucherKeys, showListCoupons])

  const couponOnchange = useCallback(
    (value: string[]) => {
      const couponErrors = cart.coupons
        ?.filter(e => e.mes)
        ?.map(e => e.coupon?.initialKey)

      const sanitized = value.filter(candidate => {
        if (!candidate) return false
        if (!couponErrors?.length) return true
        return !couponErrors.includes(candidate)
      })

      const uniqueByTarget = sanitized.reduce<string[]>(
        (accumulator, current) => {
          if (!current) return accumulator
          const segments = current.split('_')
          const target = segments[segments.length - 1] || ''

          if (target === 'orderCoupon') {
            const next = accumulator.filter(
              voucher => !voucher.endsWith('_orderCoupon')
            )
            return [...next, current]
          }

          const next = accumulator.filter(
            voucher => !voucher.endsWith(`_${target}`)
          )

          return [...next, current]
        },
        []
      )

      setCouponSelected(uniqueByTarget)
    },
    [cart.coupons]
  )

  const applyCoupon = useCallback(async () => {
    let finalVouchers = [...couponSelected]

    if (inputVoucher.trim()) {
      const validation = validateVoucherInput(inputVoucher)
      if (validation.isValid) {
        finalVouchers.push(inputVoucher.trim().toUpperCase())
      } else {
        setInputVoucherError(validation.message)
        return
      }
    }

    finalVouchers = finalVouchers.filter(
      (voucher, index, self) => self.indexOf(voucher) === index
    )

    if (finalVouchers.length === 0) {
      clearAutoVoucherState({ skipAutoApply: true })
    }

    setCouponApply(finalVouchers)
    await updateVoucher(finalVouchers, true)
  }, [
    couponSelected,
    inputVoucher,
    validateVoucherInput,
    setCouponApply,
    updateVoucher,
    clearAutoVoucherState,
  ])

  const popupClose = useCallback(() => {
    setShowListCoupons(false)
    setInputVoucher('')
    setInputVoucherError('')
    setCouponSelected([])
    setPendingVouchers([])
    const currentProductIds = collectProductIds(cart?.products)
    getCartCoupons(currentProductIds.length ? currentProductIds : undefined)
  }, [setShowListCoupons, getCartCoupons, cart?.products])

  const totalSelectedVouchers = useMemo(() => {
    const inputVoucherTrimmed = inputVoucher?.trim()
      ? [inputVoucher?.trim().toUpperCase()]
      : []
    const allVouchers = [
      ...(couponSelected || []),
      ...inputVoucherTrimmed,
      ...(pendingVouchers || []),
    ]

    const uniqueVouchers = allVouchers?.filter(
      (voucher, index, self) => self.indexOf(voucher) === index
    )

    return uniqueVouchers?.length ?? 0
  }, [couponSelected, inputVoucher, pendingVouchers])

  return (
    <PopupFrame
      open={showListCoupons}
      onClose={popupClose}
      closeText={tCommon('close')}
      type='voucher'
      className='min-h-[480px] w-full max-w-[560px] bg-white-400'
    >
      <PopupTitle
        className='border-b border-zinc-300 px-3 pt-3'
        type={'voucher'}
      >
        <div className='relative flex w-full flex-wrap items-center'>
          <p className='w-[70%] text-lg font-bold'>
            {t('voucherForYou')}
            {totalSelectedVouchers > 0 && (
              <span className='ml-2 text-sm font-normal text-primary-base'>
                ({totalSelectedVouchers} đã chọn)
              </span>
            )}
          </p>
          <div
            onClick={popupClose}
            className='absolute right-0 top-[50%] z-10 flex translate-y-[-50%] cursor-pointer items-center gap-1'
          >
            <PopupClose className=' text-dark-400' />
          </div>
        </div>
      </PopupTitle>

      <PopupBody>
        <div className='listCoupons border-grey-100 mt-[18px] flex flex-wrap justify-between gap-y-[12px] scrollbar-hide'>
          <div className='flex w-full flex-wrap items-start justify-between gap-3 px-3'>
            <div className='inputGroup flex-auto relative flex-1'>
              <input
                placeholder='Nhập mã giảm giá'
                type='text'
                name='formDiscount'
                value={inputVoucher}
                onChange={handleInputChange}
                onKeyPress={handleInputKeyPress}
                className={clsx(
                  'h-[48px] w-full rounded-[6px] border p-3 text-base font-bold text-blue-800 shadow-none placeholder:font-normal placeholder:text-dark-400/70 focus:shadow-none focus:outline-none focus:ring-0',
                  inputVoucherError || errorAuth?.status
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-grey-100 focus:border-grey-100'
                )}
                disabled={isProcessing}
              />
              {inputVoucher && (
                <button
                  className='clear absolute right-3 top-[50%] z-10 translate-y-[-50%]'
                  onClick={() => {
                    setInputVoucher('')
                    setInputVoucherError('')
                    setIsApply(false)
                  }}
                  type='button'
                  disabled={isProcessing}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={20}
                    height={20}
                    viewBox='0 0 20 20'
                    fill='none'
                  >
                    <circle cx={10} cy={10} r='9.5' fill='#B1B1B1' />
                    <path
                      d='M14.0716 12.7695C14.4319 13.1299 14.4319 13.7123 14.0716 14.0728C13.8919 14.2525 13.656 14.3428 13.4199 14.3428C13.184 14.3428 12.9481 14.2525 12.7683 14.0728L9.99902 11.3032L7.2297 14.0728C7.04998 14.2525 6.81407 14.3428 6.57816 14.3428C6.34208 14.3428 6.10617 14.2525 5.92646 14.0728C5.56618 13.7123 5.56618 13.1299 5.92646 12.7695L8.69595 10.0001L5.92646 7.23071C5.56618 6.87026 5.56618 6.28789 5.92646 5.92744C6.2869 5.56716 6.86925 5.56716 7.2297 5.92744L9.99902 8.697L12.7683 5.92744C13.1288 5.56716 13.7111 5.56716 14.0716 5.92744C14.4319 6.28789 14.4319 6.87026 14.0716 7.23071L11.3021 10.0001L14.0716 12.7695Z'
                      fill='white'
                    />
                  </svg>
                </button>
              )}
            </div>
            <button
              className='disabled:bg-grey-1400 h-[48px] w-[114px] rounded bg-primary-base px-[10px] py-0 text-fieldInput font-bold text-white disabled:outline-none'
              disabled={!isApply || isProcessing}
              type='button'
              onClick={applyInputVoucher}
            >
              {isProcessing ? <Loading /> : 'Áp dụng'}
            </button>
          </div>

          {/* Hiển thị lỗi input voucher */}
          {inputVoucherError && (
            <div className='mb-1 ml-3 w-full text-sm font-normal leading-tight text-red-500'>
              {inputVoucherError}
            </div>
          )}

          {/* Hiển thị lỗi từ API */}
          {inputVoucher &&
            cart?.coupons?.length <= 1 &&
            cart?.coupons?.filter(e => e.mes).length > 0 && (
              <div className='mb-1 ml-3 w-full text-sm font-normal leading-tight text-primary-base opacity-70'>
                {cart.coupons
                  .filter(e => e.mes)
                  .map((e, i) => (
                    <span key={`coupon-error-${i}`}>
                      {e.coupon.couponCode} - {e.mes}
                    </span>
                  ))}
              </div>
            )}

          <p className='mb-3 px-3 text-base font-normal text-neutral-600'>
            {t('voucherSubTitle')}
          </p>

          {cardCouponsMapping && cardCouponsMapping.length > 0 ? (
            <div className='main h-full max-h-[320px] w-full overflow-y-auto pl-3 pr-2'>
              <Listbox
                value={couponSelected}
                onChange={couponOnchange}
                multiple
                disabled={isProcessing}
              >
                <Listbox.Options
                  as='div'
                  static
                  className='flex w-full cursor-pointer flex-wrap items-start justify-start overflow-auto rounded-md bg-white text-base'
                >
                  <div>
                    {cardCouponsMapping.map((e: any) => (
                      <div key={e._id}>
                        <p className='mb-3 text-Headline text-dark-500'>
                          {e._id === 'orderCoupon' ? (
                            <strong>Áp dụng cho đơn hàng</strong>
                          ) : (
                            <>
                              <strong>Sản phẩm áp dụng: </strong>
                              {getTranslate(e.name)}
                            </>
                          )}
                        </p>
                        <div className='ml-[40px] flex flex-wrap gap-2 pb-2'>
                          {e.coupons &&
                            e.coupons.map((c: VoucherItem) => {
                              const voucherKey = convertCoupon(c.code, e._id)
                              const isSelected =
                                couponSelected.includes(voucherKey)
                              const isPending =
                                pendingVouchers.includes(voucherKey)

                              return (
                                <Listbox.Option
                                  as='div'
                                  key={c._id}
                                  className={clsx(
                                    'middle relative mr-1 w-full cursor-pointer text-fieldInput before:absolute before:left-[-40px] before:top-1/2 before:-translate-y-1/2 before:transform',
                                    c.mes
                                      ? `!text-yellow-500 before:content-[url('/assets/icons/couponWarning.svg')]`
                                      : isSelected || isPending
                                      ? `before:content-[url('/assets/icons/couponChecked.svg')]`
                                      : `before:content-[url('/assets/icons/couponPending.svg')]`,
                                    isPending && 'opacity-50',
                                    isProcessing && 'pointer-events-none'
                                  )}
                                  value={voucherKey}
                                  disabled={isProcessing}
                                >
                                  <div
                                    className={clsx(
                                      'voucherItem flex h-20 w-full items-center overflow-hidden rounded-lg bg-primary-second bg-opacity-5',
                                      c.mes && '!bg-yellow-500/10',
                                      isPending && 'animate-pulse'
                                    )}
                                  >
                                    <div className='voucherLeft min-w-[120px] max-w-[30%] flex-[30%]'>
                                      <div className='flex w-full flex-wrap justify-center'>
                                        <p
                                          className={clsx(
                                            'w-full text-center text-base text-primary-second',
                                            c.mes && '!text-yellow-500'
                                          )}
                                        >
                                          Giảm
                                        </p>
                                        <p
                                          className={clsx(
                                            'w-full text-center text-xl font-extrabold text-primary-second',
                                            c.mes && '!text-yellow-500'
                                          )}
                                        >
                                          {checkVoucherType(c)}
                                        </p>
                                        {c.percentValue && c.fixedValue && (
                                          <i
                                            className={clsx(
                                              'text-Voucher text-primary-second',
                                              c.mes && '!text-yellow-500'
                                            )}
                                          >
                                            (~
                                            {c.fixedValue
                                              .toLocaleString(Languages.VN, {
                                                style: 'currency',
                                                currency: 'VND',
                                              })
                                              .replace(/\s₫/g, 'đ')}
                                            )
                                          </i>
                                        )}
                                      </div>
                                    </div>
                                    <div className='voucherRight max-w-[70%] flex-[70%]'>
                                      <div className='flex w-full flex-wrap items-center pl-3 leading-4'>
                                        <p
                                          className={clsx(
                                            'mb-1.5 w-full text-sm font-bold text-primary-second',
                                            c.mes && '!text-yellow-500'
                                          )}
                                        >
                                          {dayjs(c.startTime).format(
                                            'HH:mm DD.MM'
                                          )}{' '}
                                          ~{' '}
                                          {dayjs(c.endTime).format(
                                            'HH:mm DD.MM'
                                          )}
                                        </p>
                                        <div
                                          className={clsx(
                                            'mb-0 w-full text-xs font-normal text-primary-second',
                                            c.mes && '!text-yellow-500'
                                          )}
                                        >
                                          {getTranslate(c.description)}
                                        </div>
                                        {c.minValue ? (
                                          <div
                                            className={clsx(
                                              'flex text-xs font-normal text-primary-second',
                                              c.mes && '!text-yellow-500'
                                            )}
                                          >
                                            <span>{t('minValueLabel')}</span>
                                            <span className='ml-[4px] font-bold'>
                                              {c.minValue
                                                .toLocaleString(
                                                  selectedLang as string,
                                                  {
                                                    style: 'currency',
                                                    currency: currency(
                                                      selectedLang as string
                                                    ),
                                                  }
                                                )
                                                .replace(/\s₫/g, 'đ')}
                                            </span>
                                          </div>
                                        ) : (
                                          ''
                                        )}
                                        {c.maxDiscountValue &&
                                          c.type !== VoucherTypes.FIXED && (
                                            <div
                                              className={clsx(
                                                'flex text-xs font-normal text-primary-second',
                                                c.mes && '!text-yellow-500'
                                              )}
                                            >
                                              <span>
                                                {t('maxDiscountValue')}
                                              </span>
                                              <span className='ml-[4px] font-bold'>
                                                {c.maxDiscountValue
                                                  .toLocaleString(
                                                    selectedLang as string,
                                                    {
                                                      style: 'currency',
                                                      currency: currency(
                                                        selectedLang as string
                                                      ),
                                                    }
                                                  )
                                                  .replace(/\s₫/g, 'đ')}
                                              </span>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                  {c.mes && (
                                    <span className='mt-1 text-xs text-yellow-600'>
                                      {c.mes}
                                    </span>
                                  )}
                                </Listbox.Option>
                              )
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                </Listbox.Options>
              </Listbox>
            </div>
          ) : (
            <div className='emptyVoucher flex w-full flex-wrap justify-center'>
              <ImageOptimize
                src={'../empty-image.png'}
                width={240}
                height={240}
                alt='Empty'
              />
              <div className='w-full text-center text-base font-normal leading-7 text-neutral-600'>
                Chưa có mã giảm giá phù hợp
              </div>
            </div>
          )}

          <div className='block w-full px-3 pb-2'>
            {totalSelectedVouchers > 0 && (
              <p className='text-pri-500 text-center text-xs'>
                Bạn đang chọn {totalSelectedVouchers} voucher.
                {inputVoucher.trim() &&
                  !couponSelected.some(
                    v => v.toUpperCase() === inputVoucher.trim().toUpperCase()
                  ) &&
                  ' Voucher từ ô nhập sẽ được thêm vào.'}
              </p>
            )}
            <FormButton
              className='mx-auto mt-2 h-[48px] w-full overflow-hidden rounded bg-primary-base text-fieldLabel font-bold uppercase text-white outline-0 disabled:bg-gray-400'
              disabled={isProcessing}
              onClick={applyCoupon}
              type='button'
            >
              {isProcessing && <Loading />}
              {totalSelectedVouchers > 0
                ? `Xác nhận (${totalSelectedVouchers} voucher)`
                : 'Xác nhận'}
            </FormButton>
            {(couponSelected.length > 0 || inputVoucher.trim()) && (
              <button
                className='mx-auto h-[40px] w-full overflow-hidden text-sm font-medium text-gray-700 disabled:opacity-50'
                disabled={isProcessing}
                onClick={clearCoupon}
                type='button'
              >
                {isProcessing ? (
                  <>
                    <Loading /> Đang xóa...
                  </>
                ) : (
                  'Xóa tất cả voucher'
                )}
              </button>
            )}
          </div>
        </div>
      </PopupBody>
    </PopupFrame>
  )
}

export default ModalVoucherList
